import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

/**
 * Formatters Intl localisés (Intl.DateTimeFormat + Intl.NumberFormat natifs).
 * Réactif à la locale courante — change automatiquement à chaque setLocale().
 *
 * Conventions par locale :
 *   fr-CH : "21.05.2026"  "14h30"  "CHF 1'234.50"
 *   fr    : "21/05/2026"  "14:30"  "1 234,50 €"
 *   de-CH : "21.05.2026"  "14:30"  "CHF 1'234.50"
 *   de    : "21.05.2026"  "14:30"  "1.234,50 €"
 *   en    : "May 21, 2026"  "2:30 PM"  "$1,234.50"
 *   etc.
 *
 * Pour les devises non-CHF, le code à passer en argument :
 *   fmtCurrency(1234.5, 'EUR') → "1 234,50 €" en fr / "€ 1.234,50" en nl
 */
export function useFormatters() {
  const { locale } = useI18n();

  const intlLocale = computed(() => locale.value);

  const dateFormatter = computed(() =>
    new Intl.DateTimeFormat(intlLocale.value, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }),
  );

  const dateLongFormatter = computed(() =>
    new Intl.DateTimeFormat(intlLocale.value, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }),
  );

  const timeFormatter = computed(() =>
    new Intl.DateTimeFormat(intlLocale.value, {
      hour: '2-digit',
      minute: '2-digit',
    }),
  );

  const numberFormatter = computed(() =>
    new Intl.NumberFormat(intlLocale.value),
  );

  /** Format date court (21.05.2026 / 21/05/2026 / 05/21/2026) */
  function fmtDate(input: Date | string | number): string {
    const d = input instanceof Date ? input : new Date(input);
    return dateFormatter.value.format(d);
  }

  /** Format date long (21 mai 2026 / May 21, 2026) */
  function fmtDateLong(input: Date | string | number): string {
    const d = input instanceof Date ? input : new Date(input);
    return dateLongFormatter.value.format(d);
  }

  /** Format heure (14:30 / 2:30 PM) */
  function fmtTime(input: Date | string | number): string {
    const d = input instanceof Date ? input : new Date(input);
    return timeFormatter.value.format(d);
  }

  /** Format nombre (1'234.5 fr-CH / 1.234,5 de / 1,234.5 en) */
  function fmtNumber(value: number, options?: Intl.NumberFormatOptions): string {
    if (options) {
      return new Intl.NumberFormat(intlLocale.value, options).format(value);
    }
    return numberFormatter.value.format(value);
  }

  /** Format devise — currency code en argument (CHF par défaut pour le marché initial) */
  function fmtCurrency(value: number, currency = 'CHF'): string {
    return new Intl.NumberFormat(intlLocale.value, {
      style: 'currency',
      currency,
    }).format(value);
  }

  /** Format pourcentage (12.5 → "12,5 %" en fr / "12.5%" en en) */
  function fmtPercent(value: number, fractionDigits = 1): string {
    return new Intl.NumberFormat(intlLocale.value, {
      style: 'percent',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(value / 100);
  }

  return {
    fmtDate,
    fmtDateLong,
    fmtTime,
    fmtNumber,
    fmtCurrency,
    fmtPercent,
  };
}
